import { useState } from 'react';
import { ChoiceCards, FieldHint, FieldLabel } from './formKit';
import {
  Box,
  Button,
  ButtonIcon,
  Flex,
  Select,
  Switch,
  Text,
  TextField,
} from '@backstage/ui';

/**
 * A small renderer for the JSON Schema shapes Control's specs actually use.
 *
 * # Why not react-jsonschema-form
 *
 * RJSF is already in the tree — the scaffolder renders every template with it —
 * and it was the obvious choice. It renders a flat form. What this step needs is
 * the opposite of flat: a handful of things most people change, grouped and
 * explained, with twenty more behind a disclosure. The audience is somebody who
 * has never heard of Cloud Build, and a wall of thirty-three inputs is a wall
 * whatever renders it.
 *
 * # Why Backstage UI and not MUI
 *
 * The wizard around this renders with `EXPERIMENTAL_theme: 'bui'`, so every
 * stock control is Backstage UI. These are the same components the stock
 * widgets use — `Select` here is the one the scaffolder's own SelectWidget
 * renders — so a custom field and a stock one beside it are indistinguishable.
 * Built on MUI they would be visibly different inputs in one form, which is
 * worse than the uniform old look both replaced.
 *
 * # Where the words come from
 *
 * Nothing in this file knows what any field means. Labels, help, enum wording,
 * suggestions and manual-step warnings all arrive in the manifest — help as the
 * schema's own `description`, generated from Control's Zod `.describe()`; the
 * rest as `ui.fields[name]`. A help table living here would be a second list
 * keyed on Control's field names, which is the drift D-24 exists to stop, and
 * it would fail silently: a renamed field would quietly lose its help.
 *
 * The shapes below are the complete set that appears in `serviceSpecShape` and
 * `envSpecSchema`. A shape that is not handled renders with a note rather than
 * silently dropping the field — a new spec field must be visibly unfinished,
 * never invisible.
 */

export interface JsonSchema {
  type?: string;
  title?: string;
  description?: string;
  enum?: string[];
  default?: unknown;
  items?: JsonSchema;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  additionalProperties?: JsonSchema | boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  format?: string;
  maxItems?: number;
}

/**
 * Control's per-field presentation, from `ui.fields[name]`.
 *
 * Mirrors `PipelineUiField` in Control's `lib/pipelines/manifest.ts`. Every
 * member is optional and every one is ignorable: a field with no entry renders
 * from its schema alone, which is what all of them did before this existed.
 * That is deliberate — this is the half of the contract that CAN drift, so it
 * degrades to the old behaviour rather than to an error.
 */
export interface UiField {
  label?: string;
  placeholder?: string;
  showWhen?: {
    field: string;
    equals?: string | number | boolean;
    nonEmpty?: boolean;
  };
  suggest?: string[];
  pattern?: string;
  manualStep?: string;
  options?: Record<string, { label: string; hint?: string }>;
  implies?: {
    equals?: string | number | boolean;
    defaults: Record<string, string[] | Record<string, string>>;
  };
}

/**
 * Apply one field's `implies`, if answering it triggered any.
 *
 * Nothing here knows what `oauth` is, or that `auth-secret` exists. Control
 * publishes the dependency beside the option it belongs to; this walks whatever
 * it published. A table of Control's field names living in this repository is
 * the drift D-24 exists to stop, and it would fail in the worst way — a
 * renamed option would quietly stop implying anything.
 *
 * Two rules, both of them "defaults" in the ordinary sense:
 *
 *   - Never overwrite. A list gains only members it lacks, a map only keys it
 *     lacks. Someone who has already typed their own AUTH_ALLOWED_DOMAIN keeps
 *     it.
 *   - Never remove. Unticking the box that suggested a secret does not delete
 *     it, because by then it may have been edited, and silently dropping a
 *     declaration is worse than an extra row somebody can clear.
 *
 * An unrecognised shape is left alone rather than guessed at. Control's type
 * allows a list or a map; a third shape arriving here means the two have
 * drifted, and doing nothing is the version of that which loses no answers.
 */
export function applyImplies(
  ui: UiField | undefined,
  value: JsonValue | undefined,
  values: Record<string, JsonValue>,
): Record<string, JsonValue> {
  const implies = ui?.implies;
  if (!implies || value !== (implies.equals ?? true)) return values;

  const next = { ...values };
  for (const [target, addition] of Object.entries(implies.defaults)) {
    if (Array.isArray(addition)) {
      const current = Array.isArray(next[target])
        ? (next[target] as JsonValue[])
        : [];
      const missing = addition.filter(member => !current.includes(member));
      if (missing.length) next[target] = [...current, ...missing];
    } else if (addition && typeof addition === 'object') {
      const current =
        next[target] && typeof next[target] === 'object'
          ? (next[target] as Record<string, JsonValue>)
          : {};
      const merged = { ...current };
      for (const [key, member] of Object.entries(addition)) {
        if (!(key in merged)) merged[key] = member;
      }
      if (Object.keys(merged).length > Object.keys(current).length) {
        next[target] = merged;
      }
    }
  }
  return next;
}

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [k: string]: JsonValue };

/**
 * A DOM id for a field, so its label is associated with its input.
 *
 * `prefix` scopes it without touching the LABEL: `jobs` renders the same
 * property names once per card, so ids collide and a label would point at
 * whichever input rendered first. Folding the prefix into the name instead
 * would relabel the field "Jobs 0 name".
 */
export const fieldId = (name: string, prefix?: string) =>
  `schema-field-${prefix ? `${prefix}-` : ''}${name}`;

/** Turn `publicReadStorageBuckets` into "Public read storage buckets". */
export function humanise(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Is `field` worth showing, given what has been answered so far?
 *
 * Exported because the decision belongs to whoever owns the whole form —
 * `showWhen` names a SIBLING field, which a single field renderer cannot see.
 *
 * Absent metadata means show it. Every unknown here resolves toward visible: a
 * condition naming a field that no longer exists would otherwise hide an input
 * forever, and a request that silently omits what somebody meant to set is a
 * worse outcome than one extra box.
 */
export function isRelevant(
  ui: UiField | undefined,
  values: Record<string, JsonValue>,
): boolean {
  const when = ui?.showWhen;
  if (!when) return true;
  const other = values[when.field];
  if (when.nonEmpty) {
    if (Array.isArray(other)) return other.length > 0;
    if (other && typeof other === 'object') {
      return Object.keys(other).length > 0;
    }
    return Boolean(other);
  }
  if (when.equals !== undefined) return other === when.equals;
  return true;
}

/** Fill `{name}` and `{project}` in a preview pattern. */
function interpolatePattern(
  pattern: string,
  value: string,
  context: Record<string, string>,
): string {
  return pattern.replace(/\{(\w+)\}/g, (_, key: string) =>
    key === 'value' ? value : context[key] ?? `{${key}}`,
  );
}

/**
 * "Somebody still has to do this by hand."
 *
 * Always visible, never behind a disclosure. `oauth`, `email` and `manageDomain` all
 * end in a person doing something afterwards, and that changes whether you tick
 * the box. Learning it from the run — after the request — is learning it too
 * late to act on.
 */
export function ManualStep(props: { text: string }) {
  return (
    <Flex
      gap="2"
      align="start"
      style={{
        marginTop: 6,
        marginBottom: 2,
        padding: '8px 10px',
        borderRadius: 'var(--bui-radius-2)',
        background: 'var(--ar-warn-surface, rgba(180, 120, 0, 0.08))',
        border: '1px solid var(--ar-warn-border, rgba(180, 120, 0, 0.25))',
      }}
    >
      <span aria-hidden style={{ lineHeight: 1.4 }}>
        ⚠
      </span>
      <Text variant="body-small">
        <strong>Not fully automated. </strong>
        {props.text}
      </Text>
    </Flex>
  );
}

/**
 * A label with its hint in plain sight, for the controls that draw their own
 * label. It used to be a `?` tooltip — a hint people had to know to go looking
 * for, beside the field it was meant to decide.
 */
function LabelWithHelp(props: { label: string; help?: string }) {
  return (
    <>
      <FieldLabel>{props.label}</FieldLabel>
      <FieldHint text={props.help} />
    </>
  );
}

/** Clickable suggestions. Not a closed list — typing something else is fine. */
function Suggestions(props: {
  values: string[];
  onPick: (value: string) => void;
  exclude?: string[];
}) {
  const remaining = props.values.filter(v => !props.exclude?.includes(v));
  if (remaining.length === 0) return null;
  return (
    <Flex gap="2" style={{ flexWrap: 'wrap', marginTop: 6 }}>
      <Text variant="body-small" color="secondary">
        Common:
      </Text>
      {remaining.map(value => (
        <Button
          key={value}
          size="small"
          variant="tertiary"
          onPress={() => props.onPick(value)}
        >
          {value}
        </Button>
      ))}
    </Flex>
  );
}

interface FieldProps {
  name: string;
  schema: JsonSchema;
  value: JsonValue | undefined;
  onChange: (value: JsonValue | undefined) => void;
  idPrefix?: string;
  /** Control's presentation for this field, when it published any. */
  ui?: UiField;
  /** Values `ui.pattern` may interpolate — the service name, the project. */
  context?: Record<string, string>;
}

export function SchemaFormField(props: FieldProps) {
  const { name, schema, value, onChange, idPrefix, ui, context = {} } = props;
  const label = ui?.label ?? schema.title ?? humanise(name);
  const help = schema.description;
  const manual = ui?.manualStep;

  // Enum → radios when Control wrote the options out, a select otherwise.
  //
  // Radios because the wording is the point: `data` and `ingress` are choices
  // between postures, and a collapsed select shows one line of a decision that
  // needs three. Without published options there is nothing to explain and a
  // select is the tidier control.
  if (schema.enum?.length) {
    const current = (value as string) ?? (schema.default as string) ?? '';
    const options = ui?.options;

    if (options) {
      // Cards, in the schema's enum order, which is Control's to decide (it
      // lists AWS first). Selected only when there is a value or a schema
      // default: Control publishes `cloud` with neither, so nobody is handed
      // an answer they did not pick.
      return (
        <Box>
          <ChoiceCards
            name={fieldId(name, idPrefix)}
            label={label}
            // When EVERY option carries its own hint, the schema description
            // only restates them in API terms ("aws (an AWS account for
            // <workload>-<env>)"), written as much for agents as people. If any
            // option has no hint, the description is the only explanation
            // there is, so it stays.
            description={
              Object.values(options).every(o => o.hint) ? undefined : help
            }
            value={current || undefined}
            onChange={next => onChange(next)}
            choices={schema.enum.map(option => ({
              value: String(option),
              title: options[String(option)]?.label ?? String(option),
              hint: options[String(option)]?.hint,
            }))}
          />
          {manual && <ManualStep text={manual} />}
        </Box>
      );
    }

    return (
      <Box mb="3">
        <Select
          name={fieldId(name, idPrefix)}
          label={label}
          description={help}
          options={schema.enum.map(option => ({
            value: option,
            label: ui?.options?.[option]?.label ?? option,
          }))}
          selectedKey={current}
          onSelectionChange={key => onChange(key ? String(key) : undefined)}
        />
        {manual && <ManualStep text={manual} />}
      </Box>
    );
  }

  if (schema.type === 'boolean') {
    const on = (value as boolean) ?? (schema.default as boolean) ?? false;
    return (
      <Box mb="3">
        <Switch
          label={label}
          isSelected={on}
          onChange={checked => onChange(checked)}
        />
        <Box style={{ marginTop: 8 }}>
          <FieldHint text={help} />
        </Box>
        {manual && <ManualStep text={manual} />}
      </Box>
    );
  }

  if (schema.type === 'number' || schema.type === 'integer') {
    return (
      <Box mb="3">
        <TextField
          id={fieldId(name, idPrefix)}
          name={fieldId(name, idPrefix)}
          label={label}
          value={
            value === undefined || value === null
              ? String(schema.default ?? '')
              : String(value)
          }
          onChange={v => onChange(v === '' ? undefined : Number(v))}
        />
        <Box style={{ marginTop: 8 }}>
          <FieldHint text={help} />
        </Box>
      </Box>
    );
  }

  // Array of scalars — secrets, bucket names. Entered one at a time rather
  // than as a comma-separated box: the values are identifiers, and a stray
  // space in a comma-separated list becomes a secret named " api-key".
  if (schema.type === 'array' && schema.items?.type !== 'object') {
    return (
      <StringListField
        name={fieldId(name, idPrefix)}
        label={label}
        help={help}
        placeholder={ui?.placeholder}
        suggest={ui?.suggest}
        pattern={ui?.pattern}
        context={context}
        value={(value as string[]) ?? []}
        onChange={onChange as (v: JsonValue) => void}
      />
    );
  }

  // Array of objects — `jobs`. Repeatable card.
  if (schema.type === 'array' && schema.items?.type === 'object') {
    return (
      <ObjectListField
        name={fieldId(name, idPrefix)}
        label={label}
        help={help}
        itemSchema={schema.items}
        value={(value as Record<string, JsonValue>[]) ?? []}
        onChange={onChange as (v: JsonValue) => void}
      />
    );
  }

  // Record<string,string> — env vars, substitutions, shared secrets.
  if (
    schema.type === 'object' &&
    schema.additionalProperties &&
    typeof schema.additionalProperties === 'object' &&
    !schema.properties
  ) {
    return (
      <KeyValueField
        name={fieldId(name, idPrefix)}
        label={label}
        help={help}
        value={(value as Record<string, string>) ?? {}}
        onChange={onChange as (v: JsonValue) => void}
      />
    );
  }

  if (schema.type === 'string' || schema.type === undefined) {
    const current = (value as string) ?? '';
    return (
      <Box mb="3">
        <TextField
          id={fieldId(name, idPrefix)}
          name={fieldId(name, idPrefix)}
          label={label}
          placeholder={ui?.placeholder}
          value={current}
          onChange={v => onChange(v || undefined)}
        />
        <Box style={{ marginTop: 8 }}>
          <FieldHint text={help} />
        </Box>
        {ui?.suggest && (
          <Suggestions values={ui.suggest} onPick={v => onChange(v)} />
        )}
        {manual && <ManualStep text={manual} />}
      </Box>
    );
  }

  // Not silently dropped. A field this renderer has never seen must look
  // unfinished — an invisible field is a field the portal quietly refuses to
  // let anyone ask for, which is the exact failure this whole step removes.
  return (
    <Box mb="3">
      <Text as="p" weight="bold">
        {label}
      </Text>
      <Text as="p" variant="body-small" color="secondary">
        This option has a shape the form cannot render yet. Ask in #devops, or
        set it in the terraform apps map afterwards.
      </Text>
    </Box>
  );
}

/** A list of identifiers, entered one at a time. */
export function StringListField(props: {
  name: string;
  label: string;
  help?: string;
  placeholder?: string;
  suggest?: string[];
  /** e.g. `{project}-{name}-{value}` — what each entry actually becomes. */
  pattern?: string;
  context?: Record<string, string>;
  value: string[];
  onChange: (value: JsonValue) => void;
}) {
  const [draft, setDraft] = useState('');

  const add = (raw?: string) => {
    const v = (raw ?? draft).trim();
    // Silently ignoring a duplicate beats an error: the user's intent is
    // satisfied either way, and Control de-duplicates `secrets` anyway.
    if (v && !props.value.includes(v)) props.onChange([...props.value, v]);
    setDraft('');
  };

  // The name the entry actually becomes, shown while it is still being typed.
  // A rule ("buckets are named {project}-{app}-{suffix}") is something to
  // decode; the resolved string is something to recognise.
  const preview =
    props.pattern && draft.trim()
      ? interpolatePattern(props.pattern, draft.trim(), props.context ?? {})
      : undefined;

  return (
    <Box mb="3">
      <Flex gap="2" align="end">
        <Box style={{ flex: 1 }}>
          <Flex gap="1" align="end">
            <Box style={{ flex: 1 }}>
              <TextField
                id={props.name}
                name={props.name}
                label={props.label}
                placeholder={props.placeholder}
                value={draft}
                onChange={setDraft}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    // Enter in a nested input would otherwise submit the whole
                    // wizard step, losing everything typed after it.
                    e.preventDefault();
                    add();
                  }
                }}
              />
            </Box>
          </Flex>
        </Box>
        <Button size="small" variant="secondary" onPress={() => add()}>
          Add
        </Button>
      </Flex>
      <Box style={{ marginTop: 8 }}>
        <FieldHint text={props.help} />
      </Box>
      {preview && (
        <Text
          as="p"
          variant="body-small"
          color="secondary"
          style={{ marginTop: 4, fontFamily: 'var(--ar-font-mono)' }}
        >
          {preview}
        </Text>
      )}
      {props.suggest && (
        <Suggestions
          values={props.suggest}
          exclude={props.value}
          onPick={add}
        />
      )}
      {props.value.length > 0 && (
        <Flex gap="2" style={{ flexWrap: 'wrap', marginTop: 8 }}>
          {props.value.map(item => (
            <Button
              key={item}
              size="small"
              variant="tertiary"
              aria-label={`Remove ${item}`}
              onPress={() =>
                props.onChange(props.value.filter(v => v !== item))
              }
            >
              {props.pattern
                ? interpolatePattern(props.pattern, item, props.context ?? {})
                : item}{' '}
              ✕
            </Button>
          ))}
        </Flex>
      )}
    </Box>
  );
}

/** Key/value pairs — env vars and the like. */
export function KeyValueField(props: {
  name: string;
  label: string;
  help?: string;
  value: Record<string, string>;
  onChange: (value: JsonValue) => void;
}) {
  const entries = Object.entries(props.value);

  const setEntry = (index: number, key: string, val: string) => {
    const next = entries.map((e, i) => (i === index ? [key, val] : e));
    props.onChange(Object.fromEntries(next.filter(([k]) => k)) as JsonValue);
  };

  return (
    <Box mb="3">
      <LabelWithHelp label={props.label} help={props.help} />
      <Box mt="2">
        {entries.map(([key, val], index) => (
          // Index-keyed on purpose: the key IS the editable value, so keying on
          // it makes the input lose focus on every keystroke.
          // eslint-disable-next-line react/no-array-index-key
          <Flex key={index} gap="2" align="end" style={{ marginBottom: 8 }}>
            <TextField
              name={`${props.name}-key-${index}`}
              placeholder="NAME"
              value={key}
              onChange={v => setEntry(index, v, val)}
            />
            <Box style={{ flex: 1 }}>
              <TextField
                name={`${props.name}-value-${index}`}
                placeholder="value"
                value={val}
                onChange={v => setEntry(index, key, v)}
              />
            </Box>
            <ButtonIcon
              size="small"
              variant="tertiary"
              aria-label={`Remove ${key || 'entry'}`}
              icon={<span aria-hidden>✕</span>}
              onPress={() =>
                props.onChange(
                  Object.fromEntries(
                    entries.filter((_, i) => i !== index),
                  ) as JsonValue,
                )
              }
            />
          </Flex>
        ))}
        <Button
          size="small"
          variant="secondary"
          onPress={() =>
            props.onChange({ ...props.value, '': '' } as JsonValue)
          }
        >
          Add
        </Button>
      </Box>
    </Box>
  );
}

/** A repeatable object — `jobs`, today the only one. */
export function ObjectListField(props: {
  name: string;
  label: string;
  help?: string;
  itemSchema: JsonSchema;
  value: Record<string, JsonValue>[];
  onChange: (value: JsonValue) => void;
}) {
  const properties = props.itemSchema.properties ?? {};

  const update = (index: number, key: string, val: JsonValue | undefined) => {
    props.onChange(
      props.value.map((item, i) =>
        i === index ? { ...item, [key]: val as JsonValue } : item,
      ) as JsonValue,
    );
  };

  return (
    <Box mb="3">
      <LabelWithHelp label={props.label} help={props.help} />
      <Box mt="2">
        {props.value.map((item, index) => (
          // Index-keyed: entries have no stable identity until named, and
          // keying on the name would remount the card mid-type.
          // eslint-disable-next-line react/no-array-index-key
          <Box
            key={index}
            mb="3"
            style={{
              border: '1px solid var(--bui-border-1)',
              borderRadius: 'var(--bui-radius-2)',
              padding: '12px 14px',
            }}
          >
            <Flex justify="between" align="center" style={{ marginBottom: 8 }}>
              <Text weight="bold">
                {(item.name as string) || `${props.label} ${index + 1}`}
              </Text>
              <ButtonIcon
                size="small"
                variant="tertiary"
                aria-label={`Remove ${
                  (item.name as string) || `entry ${index + 1}`
                }`}
                icon={<span aria-hidden>✕</span>}
                onPress={() =>
                  props.onChange(
                    props.value.filter((_, i) => i !== index) as JsonValue,
                  )
                }
              />
            </Flex>
            {Object.entries(properties).map(([key, schema]) => (
              <SchemaFormField
                key={key}
                name={key}
                idPrefix={`${props.name}-${index}`}
                schema={schema}
                value={item[key]}
                onChange={v => update(index, key, v)}
              />
            ))}
          </Box>
        ))}
        <Button
          size="small"
          variant="secondary"
          // Visible "Add" under a heading that already names the thing;
          // the accessible name still says what is being added, because a
          // screen reader reaches the button without the heading beside it.
          aria-label={`Add ${props.label.toLowerCase()}`}
          onPress={() => props.onChange([...props.value, {}] as JsonValue)}
        >
          Add
        </Button>
      </Box>
    </Box>
  );
}
